'use client';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import clsx from 'clsx';
import { useState } from 'react';

import { ButtonLink } from '@commons/components/Button';
import Icon from '@commons/components/Icon';
import Link from '@commons/components/Link';
import LocaleSwitcher from '@commons/components/LocaleSwitcher';
import MegaMenu from '@commons/components/PageLayout/Header/MegaMenu';
import RichText from '@commons/components/RichText';
import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import type { Alternates } from '@commons/i18n';
import PageBuilder from '@commons/pagebuilder';

import type { MainMenuLink } from '..';
import styles from './MainMenu.module.scss';

// Type definition for the QuickAccess component properties.

type Props = {
  menu: MainMenuLink[];
  alternates: Alternates;
  className?: string;
};

/**
 * Quick Access menu component.
 *
 * Renders the quick access submenu inside a MegaMenu.
 */

const QuickAccess = ({ menu = [], alternates, className }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<MainMenuLink | null>();

  const hasSublinks = activeSubMenu?.subLinks;

  const toggleMenu = (uuid: string) => {
    if (activeItem === uuid) {
      setIsOpen(!isOpen);
    } else {
      setIsOpen(true);
    }
    setActiveItem(uuid);
  };

  // Handles hover over submenu item to show the third-level menu.
  const handleSubMenuEnter = (subMenuItem: MainMenuLink) => {
    setActiveSubMenu(subMenuItem);
  };

  // Handle mouse leave for the entire menu (resets the submenu and third menu).
  const handleMenuLeave = () => {
    setActiveSubMenu(null);
  };

  // Render the first level menu inside the MegaMenu.
  const renderMenu = (subLinks: MainMenuLink[]) => (
    <NavigationMenu.List className={styles.subMenu}>
      {subLinks.map((subLink: MainMenuLink) => (
        <NavigationMenu.Item
          key={subLink.uuid}
          className={clsx(
            styles.subMenuItem,
            subLink.uuid === activeSubMenu?.uuid ? styles.active : ''
          )}
          onMouseEnter={() => handleSubMenuEnter(subLink)}
        >
          {!subLink.url ? (
            <Text
              level="h5"
              marginBottom="none"
              className={clsx(styles.link, subLink.isActive && styles.activeLink)}
            >
              {subLink.title}
            </Text>
          ) : (
            <NavigationMenu.Link asChild>
              <Link
                level="h5"
                weight="normal"
                href={subLink.url}
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                  styles.link,
                  styles.clickable,
                  subLink.isActive && styles.activeLink
                )}
              >
                {subLink.title}
              </Link>
            </NavigationMenu.Link>
          )}
          {subLink.description && (
            <Text marginTop="xxs" marginBottom="none">
              {subLink.description}
            </Text>
          )}
        </NavigationMenu.Item>
      ))}
    </NavigationMenu.List>
  );

  // Render the second level menu inside the MegaMenu.
  const renderSubMenu = (menuItem: MainMenuLink) => {
    if (menuItem.contentReference) {
      return (
        <NavigationMenu.Link asChild>
          <BaseContainer className={styles.insightsCard}>
            {PageBuilder.renderNode(menuItem.contentReference)}
          </BaseContainer>
        </NavigationMenu.Link>
      );
    }

    if (!activeSubMenu || !activeSubMenu.subLinks) return null;

    return (
      <NavigationMenu.List className={styles.subMenu}>
        {activeSubMenu.subLinks.map((thirdLink: MainMenuLink) => (
          <NavigationMenu.Item key={thirdLink.uuid} className={styles.subMenuItem}>
            <NavigationMenu.Link asChild>
              {!thirdLink.url ? (
                <Text
                  level="h5"
                  weight="normal"
                  marginBottom="none"
                  className={clsx(styles.link, thirdLink.isActive && styles.activeLink)}
                >
                  {thirdLink.title}
                </Text>
              ) : (
                <NavigationMenu.Link asChild>
                  <Link
                    href={thirdLink.url}
                    level="h5"
                    weight="normal"
                    onClick={() => setIsOpen(!isOpen)}
                    className={clsx(
                      styles.link,
                      styles.clickable,
                      thirdLink.isActive && styles.activeLink
                    )}
                  >
                    {thirdLink.title}
                  </Link>
                </NavigationMenu.Link>
              )}
            </NavigationMenu.Link>
            {thirdLink.description && (
              <Text marginTop="xxs" marginBottom="none">
                {thirdLink.description}
              </Text>
            )}
          </NavigationMenu.Item>
        ))}
      </NavigationMenu.List>
    );
  };

  // Render the main quick access menu (contains submenu and third menu).
  const renderedMenu = menu.map((item) => {
    if (!item.quickAccess) return null;

    if (item.subLinks) {
      const hasButton = item.url && item.url !== '#' && item.buttonLabel;

      const renderedItem = (
        <button
          type="button"
          className={clsx(
            styles.menuTrigger,
            styles.quickAccessTrigger,
            item.class,
            isOpen && activeItem === item.uuid && styles.open
          )}
          onClick={() => toggleMenu(item.uuid || '')}
        >
          <Text as="span" level="p" marginBottom="none">
            {item.title}
          </Text>
          <Icon type="chevronBottom" className={clsx(styles.icon, 'nav_chevron_icon')} />
        </button>
      );

      return (
        <MegaMenu
          theme="white"
          trigger={renderedItem}
          triggerMode="click"
          isOpen={isOpen && activeItem === item.uuid}
          setIsOpen={setIsOpen}
          className={styles.quickAccess}
          containerClassName={styles.menuContent}
          key={item.uuid}
        >
          <Flex
            gap="md"
            width={12}
            direction="row"
            className="h-100"
            onMouseLeave={handleMenuLeave}
          >
            <Flex
              width={4}
              border="right"
              justify="space-between"
              direction="column"
              paddingRight="md"
              onMouseEnter={handleMenuLeave}
              className={styles.menuColumn}
            >
              <Flex.Item>
                <Text as="h3" marginBottom="xs">
                  {item.title}
                </Text>
                {item.megaMenuContent && (
                  <RichText value={item.megaMenuContent} marginBottom="xs" />
                )}
                {hasButton && (
                  <ButtonLink variant="primary" href={item.url}>
                    {item.buttonLabel}
                  </ButtonLink>
                )}
              </Flex.Item>
              <Flex.Item>
                <LocaleSwitcher alternates={alternates} />
              </Flex.Item>
            </Flex>
            <Flex.Item
              width={4}
              border={activeSubMenu !== null && hasSublinks ? 'right' : undefined}
              paddingRight="md"
              className={styles.menuColumn}
            >
              {renderMenu(item.subLinks)}
            </Flex.Item>
            <Flex.Item width={4} className={styles.menuColumn}>
              {renderSubMenu(item)}
            </Flex.Item>
          </Flex>
        </MegaMenu>
      );
    }

    return (
      <NavigationMenu.Item key={item.uuid} asChild>
        <NavigationMenu.Link asChild>
          <Link href={item.url} className={styles.menuTrigger}>
            {item.title}
          </Link>
        </NavigationMenu.Link>
      </NavigationMenu.Item>
    );
  });

  return (
    <NavigationMenu.Root asChild aria-label={undefined}>
      <div className={clsx(isOpen && 'activeDesktopMenu', className)}>
        <NavigationMenu.List asChild>
          <Flex gap="sm" align="center" className="h-100">
            {renderedMenu}
          </Flex>
        </NavigationMenu.List>
      </div>
    </NavigationMenu.Root>
  );
};

export default QuickAccess;
